import {announce} from '@github-ui/aria-live'
import safeStorage from '@github-ui/safe-storage'
import {Banner, Button} from '@primer/react'
import {useEffect, useRef, useState} from 'react'

import {restoreFocusAfterDismiss} from '../../restore-focus-after-dismiss'
import type {StaleSessionBannerProps, StaleSessionEvent, StaleSessionReason} from './types'

const CHANNEL_NAME = 'stale-session'
const STORAGE_KEY = 'logged-in'
const localStorage = safeStorage('localStorage')

const MESSAGE_BY_REASON = {
  SIGNED_IN: 'You signed in with another tab or window.',
  SIGNED_OUT: 'You signed out in another tab or window.',
  SWITCHED: 'You switched accounts on another tab or window.',
} as const

/** Converts a structured app-shell event to the legacy cross-tab transport value. */
function getStaleSessionReason(event: StaleSessionEvent | undefined): StaleSessionReason | undefined {
  if (!event) return undefined
  if (event.type === 'SIGNED_IN' || event.type === 'SIGNED_OUT') return event.type
  return `SWITCHED:${event.fromUserId}:${event.toUserId}`
}

/** Parses a cross-tab transport value into a structured event, rejecting malformed values. */
function getStaleSessionEvent(value: unknown): StaleSessionEvent | undefined {
  if (value === 'SIGNED_IN' || value === 'SIGNED_OUT') return {type: value}
  if (typeof value !== 'string') return undefined

  const [type, fromUserId, toUserId, extra] = value.split(':')
  if (type !== 'SWITCHED' || fromUserId === undefined || toUserId === undefined || extra !== undefined) return undefined

  return {type, fromUserId, toUserId}
}

/** Notifies other tabs that the current tab changed the viewer's session. */
export function broadcastStaleSessionChange(reason: StaleSessionReason, channel?: BroadcastChannel | null) {
  const ownsChannel = channel === undefined
  let sender = channel

  if (channel !== null && (channel || typeof BroadcastChannel === 'function')) {
    try {
      sender ??= new BroadcastChannel(CHANNEL_NAME)
      sender.postMessage(reason)
      return
    } catch {
      // Ignore
    } finally {
      if (ownsChannel) sender?.close()
    }
  }

  localStorage.setItem(STORAGE_KEY, reason)
}

/** Global warning and submission guard for sessions changed in another browser tab. */
export function StaleSessionBanner({staleSession, returnFocusIds}: StaleSessionBannerProps) {
  const [sessionEvent, setSessionEvent] = useState<StaleSessionEvent | null>(null)
  const [dismissed, setDismissed] = useState(false)
  const bannerRef = useRef<HTMLElement>(null)
  const staleRef = useRef(false)
  const originalUserIdRef = useRef<string | null>(null)
  const channelRef = useRef<BroadcastChannel | null>(null)
  const staleSessionReason = getStaleSessionReason(staleSession?.event)

  useEffect(() => {
    const handleSessionChange = (nextReason: unknown) => {
      const nextEvent = getStaleSessionEvent(nextReason)
      if (!nextEvent) return

      staleRef.current = true
      setDismissed(false)

      if (nextEvent.type === 'SWITCHED') {
        // Keep the first account so switching back to it can clear the stale-session warning.
        if (originalUserIdRef.current === nextEvent.toUserId) {
          originalUserIdRef.current = null
          staleRef.current = false
          setSessionEvent(null)
          return
        }
        if (originalUserIdRef.current === null) originalUserIdRef.current = nextEvent.fromUserId
      }

      announce(`${MESSAGE_BY_REASON[nextEvent.type]} Reload to refresh your session.`)
      setSessionEvent(nextEvent)
    }

    const handleStorage = (event: StorageEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.storageArea === window.localStorage && event.key === STORAGE_KEY) {
        handleSessionChange(event.newValue)
        localStorage.removeItem(STORAGE_KEY)
      }
    }

    let channel: BroadcastChannel | null = null
    if (typeof BroadcastChannel === 'function') {
      try {
        channel = new BroadcastChannel(CHANNEL_NAME)
        channel.onmessage = event => handleSessionChange(event.data)
      } catch {
        channel = null
      }
    }
    channelRef.current = channel

    window.addEventListener('storage', handleStorage)

    return () => {
      channelRef.current = null
      channel?.close()
      window.removeEventListener('storage', handleStorage)
    }
  }, [])

  useEffect(() => {
    // Broadcast through the listener channel so this tab does not receive its own message.
    if (staleSessionReason) broadcastStaleSessionChange(staleSessionReason, channelRef.current)
  }, [staleSessionReason])

  useEffect(() => {
    const preventStaleSubmit = (event: SubmitEvent) => {
      if (staleRef.current) {
        event.preventDefault()
        event.stopPropagation()
      }
    }

    // Capture submissions before application handlers can act on a stale session.
    document.addEventListener('submit', preventStaleSubmit, true)
    return () => document.removeEventListener('submit', preventStaleSubmit, true)
  }, [])

  if (!sessionEvent || dismissed) return null

  const dismiss = () => {
    restoreFocusAfterDismiss(bannerRef.current, returnFocusIds)
    setDismissed(true)
  }

  return (
    <Banner
      ref={bannerRef}
      variant="warning"
      title="Session changed"
      hideTitle
      description={
        <>
          {MESSAGE_BY_REASON[sessionEvent.type]}{' '}
          <Button variant="link" onClick={() => window.location.reload()}>
            Reload
          </Button>{' '}
          to refresh your session.
        </>
      }
      onDismiss={dismiss}
      flush
    />
  )
}
